<template>
  <v-dialog
    v-model="dialog"
    light
    width="800"
    @input="closeDialog"
  >
    <v-card
      light
      class="pa-6"
      cols="12"
    >
      <v-form
        ref="form"
        v-model="isFormValid"
      >
        <v-container>
          <v-row>
            <v-col
              cols="12"
              md="12"
            >
              <div class="mb-6 text-h5">
                {{ modalTitle }}
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-0 mb-2">
            <v-col
              cols="12"
              md="12"
            >
              <div class="form-dash-modal__rules">
                <span>
                  {{ $t('AccessModal.label.important') }}
                </span>
                <ul>
                  <li>{{ $t('AccessModal.label.firstNote') }}</li>
                  <li>{{ $t('AccessModal.label.secondNote') }}</li>
                  <li>{{ $t('AccessModal.label.thirdNote') }}</li>
                </ul>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="6"
              class="py-0"
            >
              <v-text-field
                v-model="accountId"
                outlined
                dense
                :rules="existsAndIsNumber"
                :persistent-placeholder="isFormToEdit"
                :hint="displayAccHint ? this.$t('AccessModal.label.message.error.invalidInfo') : ''"
                :persistent-hint="false"
                :disabled="isFormToEdit"
                :label="$t('AccessModal.label.input.accountId')"
                @keyup="validateAccMaxLength()"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              class="py-0"
            >
              <v-text-field
                v-model="universeId"
                outlined
                dense
                :rules="existsAndIsNumber"
                :hint="displayUniHint ? this.$t('AccessModal.label.message.error.invalidInfo') : ''"
                :persistent-placeholder="isFormToEdit"
                :disabled="isFormToEdit"
                :label="$t('AccessModal.label.input.universeId')"
                @keyup="validateUniMaxLength(universeId)"
              />
            </v-col>

            <v-col
              cols="12"
              md="12"
              class="pb-0"
            >
              <v-combobox
                v-model="firstAccess"
                outlined
                :dense="!isThereFirstAccessUser"
                :disabled="!ableFirstAccessUserToEdit()"
                :rules="emailAccessRules"
                :items="[]"
                :label="$t('AccessModal.label.input.usersAccess')"
                :hint="$t('AccessModal.label.message.hint.email')"
                multiple
                small-chips
                deletable-chips
                append-icon=""
                :search-input.sync="newFirstAccess"
                @update:search-input="splitFirstAccessUser"
              />
            </v-col>

            <v-col
              cols="12"
              md="12"
              class="py-0"
            >
              <div>
                <div class="text-h6">
                  {{ $t('AccessModal.label.title.environment') }}
                </div>
                <v-radio-group
                  v-model="environment"
                  mandatory
                  :disabled="isFormToEdit"
                >
                  <v-radio
                    key="production"
                    :label="$t('AccessModal.label.input.production')"
                    :value="false"
                  />
                  <v-radio
                    key="staging"
                    :label="$t('AccessModal.label.input.staging')"
                    :value="true"
                  />
                </v-radio-group>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="12"
              class="py-0"
            >
              <div>
                <div class="text-h6">
                  {{ $t('AccessModal.label.title.contractModel') }}
                </div>
                <v-radio-group
                  v-model="contractModel"
                  mandatory
                  class="py-0"
                >
                  <v-radio
                    key="contract"
                    :label="$t('AccessModal.label.input.contract')"
                    :value="false"
                  />
                  <v-radio
                    key="trial"
                    :label="$t('AccessModal.label.input.trial')"
                    :value="true"
                  />
                </v-radio-group>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="6"
              class="py-0"
            >
              <v-menu
                v-if="contractModel"
                :close-on-content-click="true"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="computedDateFormatted"
                    :label="$t('AccessModal.label.input.expirationDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    :rules="contractModelRules"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="expiresTrialDate"
                />
              </v-menu>
            </v-col>

            <v-col
              v-if="isFormToEdit"
              cols="12"
              md="12"
              class="pt-4 py-0"
            >
              <div>
                <div class="text-h6 mb-1">
                  {{ $t('AccessModal.label.title.otherEditors') }}
                </div>

                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.mail') }}
                        </th>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.onboarding') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(editor, key) in otherEditors"
                        :key="key"
                      >
                        <td>{{ editor.email }}</td>
                        <td>{{ editor.onboardingStatus.value }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-col>

            <v-col
              v-if="isFormToEdit"
              cols="12"
              md="12"
              class="pt-4 py-0"
            >
              <div>
                <div class="text-h6 mb-1">
                  {{ $t('AccessModal.label.title.lastUpdate') }}
                </div>

                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.user') }}
                        </th>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.mail') }}
                        </th>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.updatedDate') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ dashboard.audit.updatedBy }}</td>
                        <td>{{ dashboard.audit.updatedByEmail }}</td>
                        <td>{{ toDate(dashboard.audit.updatedDate) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-col>

            <v-col
              v-if="isFormToEdit"
              cols="12"
              md="12"
              class="py-0"
            >
              <div>
                <div class="text-h6 mb-1">
                  {{ $t('AccessModal.label.title.createdDate') }}
                </div>

                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.user') }}
                        </th>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.mail') }}
                        </th>
                        <th class="text-left">
                          {{ $t('AccessModal.label.table.createdDate') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ dashboard.audit.createdBy }}</td>
                        <td>{{ dashboard.audit.createdByEmail }}</td>
                        <td>{{ toDate(dashboard.audit.createdDate) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-row
        justify="end"
        no-gutters
      >
        <v-btn
          rounded
          outlined
          color="secondary"
          class="mr-2"
          :loading="loadingBtn"
          :disabled="loadingBtn"
          @click="closeDialog"
        >
          {{ $t('AccessModal.label.button.cancel') }}
        </v-btn>
        <v-btn
          rounded
          color="primary"
          depressed
          :loading="loadingBtn"
          :disabled="loadingBtn"
          @click="validateForm"
        >
          {{ primaryButtonMessage }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import formModal from '@/mixins/form-modal';
import firebase from '@/plugins/firebase';

export default {
  name: 'AccessModal',
  mixins: [
    formModal,
  ],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    dashboard: {
      type: Object,
      default: () => ({}),
    },
    modalTitle: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      firstUserData: [],
      emailAccessRules: [
        v => (v && v.every(vv => Boolean(vv))) || this.$t('FormModal.label.message.error.addEmail'),
        v => (v && v.every(vv => /\S+@\S+\.\S+/u.test(vv))) || this.$t('FormModal.label.message.error.addAllEmails'),
        v => (v.length > 0) || this.$t('FormModal.label.message.error.addEmail'),
      ]
    };
  },
  computed: {
    ...mapState('misc', [
      'accessModal',
    ]),
    dialog: {
      get() {
        return this.accessModal;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'accessModal',
          with: newValue,
        });
      },
    },
    firstAccess: {
      get() {
        return this.firstUserData;
      },
      set(newValue) {
        this.firstUserData = newValue;

        this.firstAccessUser = this.stringArrayToObjectArray(this.firstUserData, true);
      },
    },
    otherEditors() {
      return this.users.filter(({ admin }) => !admin);
    }
  },
  watch: {
    firstAccessUser() {
      this.updateFirstAccesUser();
    }
  },
  mounted() {
    this.updateFirstAccesUser();
    this.users = this.dashboard?.users || [];
    this.$refs.form.resetValidation();
  },
  methods: {
    closeDialog() {
      this.mutateMisc({
        property: 'accessModal',
        with: false,
      });
    },
    async validateFormAndCreateAccess() {
      if (!this.$refs.form.validate()) {
        return false;
      }

      this.loadingBtn = true;

      const email = this.firstAccessUser[0]?.length !== 0 ? this.firstAccessUser[0].email : '';
      this.firstAccessUser = [this.emailToUserObject(email, true, true)];

      const dashboard = {
        expiresTrialDate: this.contractModel ? firebase.firestore.Timestamp.fromDate(new Date(this.expiresTrialDate)) : null,
        users: [this.firstAccessUser[0]?.length !== 0 ? this.firstAccessUser[0] : {}],
        userEmails: [this.firstAccessUser[0].email],
        onboardingStatus: {
          key: 'TO_DO',
          value: 'Não iniciado',
        },
        createdByAccessPage: true,
        audit: {
          createdBy: this.user.name,
          createdByEmail: this.user.email,
          createdDate: firebase.firestore.Timestamp.fromDate(new Date()),
          updatedBy: '',
          updatedByEmail: '',
          updatedDate: '',
        }
      };

      await this.createDashboard({
        dashboard,
        accountId: this.accountId,
        universeId: this.universeId,
        isStaging: this.environment,
      });

      this.$nextTick(() => {
        this.loadingBtn = false;

        this.closeDialog();
      });
    },
    async validateFormAndUpdateAccess() {
      if (!this.$refs.form.validate()) {
        return false;
      }
      this.loadingBtn = true;

      const firstUser = this.firstAccessUser[0] || {};
      const isDashboardInOnboarding = Boolean(firstUser?.onboardingStatus?.key === 'TO_DO');

      const removeAdmin = Boolean(Object.keys(firstUser).length === 0);

      if (removeAdmin) {
        this.users = this.users.filter(({ admin }) => !admin);
      }

      const users = isDashboardInOnboarding ? [firstUser] : this.users;
      const userEmails = users.map(user => user.email);

      await this.editPermission({
        universeId: this.dashboard.universeId,
        dashboardId: this.dashboard.dashboardId,
        expiresTrialDate: this.contractModel ? firebase.firestore.Timestamp.fromDate(new Date(this.expiresTrialDate)) : null,
        users,
        userEmails,
        audit: {
          updatedBy: this.user.name,
          updatedByEmail: this.user.email,
          updatedDate: firebase.firestore.Timestamp.fromDate(new Date()),
        },
      });

      this.$nextTick(() => {
        this.loadingBtn = false;

        this.closeDialog();
      });
    },
    validateForm() {
      return this.isFormToEdit ? this.validateFormAndUpdateAccess() : this.validateFormAndCreateAccess();
    },
    ableFirstAccessUserToEdit() {
      const isEditableFirstAccess = Boolean(!['DOING', 'DONE'].includes(this.dashboard?.onboardingStatus?.key));

      return this.isFormToEdit ? isEditableFirstAccess : true;
    },
    updateFirstAccesUser() {
      const isThereEmptyObject = Object.keys(this.firstAccessUser?.[0] || {}).length === 0;

      this.firstUserData = this.firstAccessUser && !isThereEmptyObject
        ? this.firstAccessUser.map(({ email }) => email)
        : [];
    }
  },
};
</script>

<style scoped lang="scss">
  .form-dash-modal {
    &__rules {
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-sm);
      color: var(--ds-color-text-2);
    }
  }

  .v-input {
    padding: 0;
    margin-top: var(--ds-spacing-stack-nano);
  }
</style>
